// @ts-nocheck
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import FileIcon from "@insite/mobius/Icons/File";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@store/MobiusOverrides/Button/Button";
import breakpointUtility from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    infoText = "infoText",
    fileUrl = "fileUrl",
    thumbnailImageUrl = "thumbnailImageUrl",
    altText = "altText",
    viewButtonLabel = "viewButtonLabel",
    alignment = "alignment",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.infoText]: string;
        [fields.fileUrl]: string;
        [fields.thumbnailImageUrl]: string;
        [fields.altText]: string;
        [fields.viewButtonLabel]: string;
        [fields.alignment]: string;
        [fields.customCSS]: string;
    };
}

export interface FileStyles {
    infoWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
    placeholderIcon?: IconPresentationProps;
    titleLink?: LinkPresentationProps;
    title?: TypographyPresentationProps;
    infoText?: TypographyPresentationProps;
    actionWrapper?: InjectableCss;
    button?: ButtonPresentationProps;
}

export const fileStyles: FileStyles = {
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(10)};
            min-height: ${pxToRem(150)};
            padding: ${pxToRem(20)};
            background: ${getColor("common.background")};
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            height: ${pxToRem(270)};
            padding: ${pxToRem(30)};
            background: ${getColor("common.borderLight")};
        `,
    },
    imageLink: {
        css: css`
            transition: ${transitionHoverStyles("scale")};

            ${breakpointUtility("md", "up")} {
                &:hover,
                &:focus {
                    scale: 1.1;
                }
            }
        `,
    },
    img: {
        css: css`
            height: auto;
            max-width: 100%;
            box-shadow: ${getProp("theme.shadows.e80")};
        `,
    },
    placeholderIcon: {
        src: FileIcon,
        size: 144,
        color: "placeholder.main",
        css: css`
            opacity: 0.6;
        `,
    },
    titleLink: {
        typographyProps: {
            weight: "bold",
        },
    },
    title: {
        variant: "h5",
        forwardAs: "h2",
        color: "secondary.contrast",
    },
    infoText: {
        size: 14,
        color: "text.accent",
    },
    actionWrapper: {
        css: css`
            margin-top: auto;
        `,
    },
    button: {
        variant: "tertiary",
        sizeVariant: "small",
    },
};

const styles = fileStyles;

export const File: FC<OwnProps> = ({ fields }) => {
    const customCssWrapper: InjectableCss = {
        css: css`
            border-radius: ${pxToRem(16)};
            box-shadow: ${getProp("theme.shadows.e60")};
            ${fields.customCSS}
        `,
    };

    const infoWrapperStyles = {
        css: css`
            align-items: ${fields.alignment || "center"};
            ${styles.infoWrapper?.css}
        `,
    };

    return (
        <StyledWrapper className="tile-wrapper" {...customCssWrapper}>
            <StyledWrapper className="img-wrapper" {...styles.imageWrapper}>
                <Link className="img-link" href={fields.fileUrl} target="_blank" {...styles.imageLink}>
                    {fields.thumbnailImageUrl ? (
                        <Img
                            className="img"
                            src={fields.thumbnailImageUrl}
                            altText={fields.altText ? fields.altText : translate("File Thumbnail Image")}
                            loading="lazy"
                            {...styles.img}
                        />
                    ) : (
                        <Icon className="placeholder-icon" {...styles.placeholderIcon}></Icon>
                    )}
                </Link>
            </StyledWrapper>
            <StyledWrapper className="info-wrapper" {...infoWrapperStyles}>
                <Link className="title-link" href={fields.fileUrl} target="_blank" {...styles.titleLink}>
                    <Typography className="title" {...styles.title}>
                        {fields.title}
                    </Typography>
                </Link>
                {fields.infoText && (
                    <Typography className="information-text" {...styles.infoText}>
                        {fields.infoText}
                    </Typography>
                )}
                {fields.viewButtonLabel && (
                    <StyledWrapper className="action-wrapper" {...styles.actionWrapper}>
                        <Button className="view-button" {...styles.button}>
                            {fields.viewButtonLabel}
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.tile-wrapper {
}

.img-link {
}

.img-wrapper {
}

.img {
}

.placeholder-icon {
}

.info-wrapper {
}

.title-link {
}

.title {
}

.information-text {
}

.action-wrapper {
}

.view-button {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: File,
    definition: {
        group: "Basic",
        icon: "file",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
                isRequired: true,
            },
            {
                name: fields.infoText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.fileUrl,
                fieldType: "Contextual",
                editorTemplate: "FilePickerField",
                defaultValue: "",
                tab: basicTab,
                isRequired: true,
            },
            {
                name: fields.thumbnailImageUrl,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                tooltipPosition: "left",
                tooltip: "Recommended image size : Width: 155px / Height: 200px",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.altText,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.viewButtonLabel,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "View file",
                tab: basicTab,
            },
            {
                name: fields.alignment,
                displayName: "Text Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
